import { IconChevronDown } from '@developerz/ui';
import { type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import type { UtilBox } from '../../lib/fleetUtilization';
import type { CardView } from '../../lib/machine-card';
import type { MachineHandlers } from '../../lib/machine-panel';
import { MissionCardMenu } from './MissionCardMenu';
import { StateMark } from './StateMark';

// The card's header (#6785, prototype `mission` header, `:2448-2449`): the live dot, the
// name (a button opening the details panel, for every viewer), `<8-char id> · <server> ·
// <health>` (never the hostname, #6443), "From <org>" on another org's machine, the state
// chip (hidden in Full while the state is loud: the banner says it), the chevron and the
// "..." menu.
//
// THE DOT IS THE ONE LIVE SURFACE: Autonomous wears `.status__dot` (`ui/_badge.scss`); every
// other state the neutral or red `.machine-card__dot`. Healthy is neutral, never green.

export type MissionCardHeaderProps = MachineHandlers & {
  readonly box: UtilBox;
  readonly view: CardView;
  readonly canWrite: boolean;
  readonly simplified: boolean;
  readonly bodyId: string;
  readonly onSimplify: (simplified: boolean) => void;
  readonly notify: (message: string, tone?: 'success' | 'error') => void;
  /** The menu's Send work (#6770): opens this card's form. */
  readonly onSendWork?: ((box: UtilBox) => void) | undefined;
  /** Move work and Drain machine (#6772); absent, the menu offers neither. */
  readonly onMoveWork?: ((box: UtilBox, drain: boolean) => void) | undefined;
};

export function MissionCardHeader(props: MissionCardHeaderProps): JSX.Element {
  const name = (): string => props.box.display_name;
  return (
    <header class="mission-card__head">
      <Show
        when={props.view.state === 'autonomous'}
        fallback={
          <span
            class="machine-card__dot mission-card__dot"
            data-status={props.box.verdict.status}
            aria-hidden="true"
          />
        }
      >
        <span class="status__dot mission-card__dot" aria-hidden="true" />
      </Show>
      <div class="mission-card__id">
        <button type="button" class="machine-card__name" onClick={() => props.onManage(props.box)}>
          {name()}
        </button>
        <p class="machine-card__sub">
          <span>{props.view.identity.id}</span>
          <Show when={props.view.identity.server}>{(server) => <span>{server()}</span>}</Show>
          <span
            class="machine-card__verdict"
            data-tone={props.view.identity.healthTone}
            title={t('fleet.machines.card_health_title', { health: props.view.identity.health })}
          >
            {props.view.identity.health}
          </span>
        </p>
        <Show when={props.view.fromOrg}>
          {(org) => (
            <p class="machine-card__from">{t('fleet.machines.from_org', { org: org() })}</p>
          )}
        </Show>
      </div>
      <Show when={props.simplified || !props.view.loud}>
        <span class="mission-card__chip" data-tone={props.view.tone}>
          <StateMark state={props.view.state} />
          {props.view.label}
        </span>
      </Show>
      <button
        type="button"
        class="mission-card__chevron"
        aria-expanded={!props.simplified}
        aria-controls={props.bodyId}
        aria-label={t(
          props.simplified ? 'fleet.machines.card_expand' : 'fleet.machines.card_collapse',
          {
            name: name(),
          },
        )}
        title={t(
          props.simplified
            ? 'fleet.machines.card_expand_title'
            : 'fleet.machines.card_collapse_title',
        )}
        onClick={() => props.onSimplify(!props.simplified)}
      >
        <IconChevronDown />
      </button>
      <MissionCardMenu
        box={props.box}
        canWrite={props.canWrite}
        size={{ simplified: props.simplified, onSimplify: props.onSimplify }}
        notify={props.notify}
        onSendWork={props.onSendWork}
        onMoveWork={props.onMoveWork}
        onManage={props.onManage}
        onRename={props.onRename}
        onRemove={props.onRemove}
        onDrain={props.onDrain}
        onUndrain={props.onUndrain}
      />
    </header>
  );
}
